import { useMemo, useRef } from "react";

import { collectPolygonLocations } from "@ryde/controls";
import { getVisibleAreaPolygons, IAreaPolygonItem } from "@ryde/layout";
import { ILocationInfo } from "@ryde/shared";

interface IUseAreaPolygonsFitLocationsArgs {
  areas: IAreaPolygonItem[];
  hiddenIds?: ReadonlySet<number>;
}

/**
 * The points the areas map frames itself on: every vertex of every area currently drawn.
 *
 * Cached against the *set* of visible areas rather than their geometry, so the map re-frames when the grid
 * selection or the legend's show/hide changes and holds still otherwise. That is what stops it jumping
 * when an area is picked up for reshaping — which takes it out of the viewer layer — and again when the
 * save re-fetches every area with fresh geometry. The Leaflet map kept the same rule, as an id signature
 * guarding its `fitBounds`.
 */
export const useAreaPolygonsFitLocations = ({
  areas,
  hiddenIds,
}: IUseAreaPolygonsFitLocationsArgs): ILocationInfo[] => {
  const visibleAreas = useMemo(
    () => getVisibleAreaPolygons(areas, hiddenIds),
    [areas, hiddenIds],
  );
  const signature = visibleAreas.map(({ id }) => id).join(",");
  const cacheRef = useRef<{ signature: string; locations: ILocationInfo[] }>({
    signature: "",
    locations: [],
  });

  // Recomputed during render rather than in an effect: from an effect the fit would run once against the
  // stale points and again against the fresh ones, which reads as the map jumping twice.
  if (signature !== cacheRef.current.signature) {
    cacheRef.current = {
      signature,
      locations: collectPolygonLocations(visibleAreas),
    };
  }

  return cacheRef.current.locations;
};
